import { App, Button, Dropdown, Empty, Spin, Switch, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PasskeyCredentialDto, PasskeyStatusResponse } from '../../interfaces/UserApiService';
import { UserService } from '../../services/userApiService';
import { getApiErrorDetail } from '../../utils/apiErrorCode';
import { formatDate } from '../../utils/utils';
import { isPasskeySupported } from '../../utils/webauthnClient';
import ModalLayout from '../../layouts/ModalLayout';
import { Icon } from '../Core/Icon';
import PasskeyCreateForm from './PasskeyCreateForm';
import PasskeyEditForm from './PasskeyEditForm';

const userService = new UserService();

export interface PasskeysFormProps {
  // Triggered when the modal is closed without required follow-up.
  onClose?: () => void;
  // Optional: parent can provide a callback to refresh data after successful mutation.
  onSuccess?: () => void;
}

// Backend may omit transports or send a comma-separated string instead of string[].
function normalizePasskeyTransports(transports: unknown): string[] {
  if (transports == null) return [];
  if (Array.isArray(transports)) {
    return transports.filter((item): item is string => typeof item === 'string' && item.length > 0);
  }
  if (typeof transports === 'string') {
    return transports
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }
  return [];
}

// Helper: Localize and summarize transports string for display.
function transportSummary(transports: unknown, translate: (key: string) => string): string {
  const list = normalizePasskeyTransports(transports);
  if (list.length === 0) {
    return translate('modal.passkeysForm.transportUnknown');
  }
  const sorted = [...list].sort().join(',');
  const map: Record<string, string> = {
    ble: translate('modal.passkeysForm.transportBle'),
    hybrid: translate('modal.passkeysForm.transportHybrid'),
    'hybrid,internal': translate('modal.passkeysForm.transportHybridInternal'),
    internal: translate('modal.passkeysForm.transportInternal'),
    nfc: translate('modal.passkeysForm.transportNfc'),
    usb: translate('modal.passkeysForm.transportUsb'),
    'usb,nfc': translate('modal.passkeysForm.transportUsbNfc'),
  };
  return map[sorted] ?? list.join(', ');
}

// Main passkey management UI component.
export default function PasskeysForm({ onClose, onSuccess }: PasskeysFormProps) {
  const { t, i18n } = useTranslation();
  const { message, modal } = App.useApp();
  const locale = i18n.language;

  // Holds latest passkey status and loading state for UI.
  const [passkeyStatus, setPasskeyStatus] = useState<PasskeyStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetches current passkey info from server. Handles errors.
  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const data = await userService.passkeyStatus();
      setPasskeyStatus(data);
    } catch (error: unknown) {
      message.error(getApiErrorDetail(error) ?? t('modal.passkeysForm.loadError'));
      setPasskeyStatus(null);
    } finally {
      setLoading(false);
    }
  }, [message, t]);

  // On mount: check for WebAuthn support, then load data.
  useEffect(() => {
    if (!isPasskeySupported()) {
      setLoading(false);
      return;
    }
    void loadStatus();
  }, [loadStatus]);

  // UI constant: max credentials shown/quota allowed. (Backend might check differently.)
  const maxPasskeys = 10;

  // Show new-passkey modal dialog.
  const openAddPasskeyModal = useCallback(() => {
    const modalInstance = modal.confirm({
      content: (
        <PasskeyCreateForm
          onClose={() => modalInstance.destroy()}
          onSuccess={() => {
            modalInstance.destroy();
            void loadStatus();
            onSuccess?.();
          }}
        />
      ),
      footer: null,
      title: t('modal.passkeyCreateForm.title'),
    });
  }, [loadStatus, modal, onSuccess, t]);

  // Show edit-name dialog for particular credential.
  const openRenamePasskeyModal = useCallback(
    (credential: PasskeyCredentialDto) => {
      const modalInstance = modal.confirm({
        content: (
          <PasskeyEditForm
            key={credential.credentialId}
            credentialId={credential.credentialId}
            initialDeviceName={credential.deviceName}
            onClose={() => modalInstance.destroy()}
            onSuccess={() => {
              modalInstance.destroy();
              void loadStatus();
              onSuccess?.();
            }}
          />
        ),
        footer: null,
        title: t('modal.passkeyEditForm.title'),
        width: 640,
      });
    },
    [loadStatus, modal, onSuccess, t],
  );

  // Remove passkey and reload, displaying toast for success/failure.
  const removeCredential = useCallback(
    async (credential: PasskeyCredentialDto) => {
      try {
        await userService.passkeyRemove(credential.credentialId);
        message.success(t('modal.passkeysForm.removeSuccess'));
        await loadStatus();
        onSuccess?.();
      } catch (error: unknown) {
        message.error(getApiErrorDetail(error) ?? t('modal.passkeysForm.removeError'));
      }
    },
    [loadStatus, message, onSuccess, t],
  );

  // Toggle passwordless "discoverable" usage of a credential, then reload.
  const togglePasswordless = useCallback(
    async (credential: PasskeyCredentialDto, enabled: boolean) => {
      try {
        await userService.passkeyTogglePasswordless(credential.credentialId, { enabled });
        message.success(
          enabled
            ? t('modal.passkeysForm.passwordlessEnabled')
            : t('modal.passkeysForm.passwordlessDisabled'),
        );
        await loadStatus();
        onSuccess?.();
      } catch (error: unknown) {
        message.error(getApiErrorDetail(error) ?? t('modal.passkeysForm.toggleError'));
      }
    },
    [loadStatus, message, onSuccess, t],
  );

  // Build menu drop-down items for each row.
  const rowMenu = useCallback(
    (credential: PasskeyCredentialDto): MenuProps['items'] => [
      {
        key: 'rename',
        label: t('modal.passkeysForm.rename'),
        onClick: () => {
          openRenamePasskeyModal(credential);
        },
      },
      { type: 'divider' },
      {
        danger: true,
        key: 'remove',
        label: t('modal.passkeysForm.remove'),
      },
    ],
    [openRenamePasskeyModal, t],
  );

  // Memoize: detect if passkeys are unsupported in this browser.
  const unsupported = useMemo(() => !isPasskeySupported(), []);

  // Show warning message if browser is incompatible.
  if (unsupported) {
    return (
      <ModalLayout maxHeight="70vh">
        <ModalLayout.Content>
          <div className="py-6">
            <Typography.Paragraph type="warning">
              {t('modal.passkeysForm.browserUnsupported')}
            </Typography.Paragraph>
          </div>
        </ModalLayout.Content>
        {onClose ? (
          <ModalLayout.Footer>
            <div className="flex justify-end gap-2">
              <Button onClick={onClose}>{t('common.close')}</Button>
            </div>
          </ModalLayout.Footer>
        ) : null}
      </ModalLayout>
    );
  }

  // Show centered loading spinner while fetching.
  if (loading) {
    return (
      <ModalLayout maxHeight="70vh">
        <ModalLayout.Content>
          <div className="flex justify-center py-12">
            <Spin />
          </div>
        </ModalLayout.Content>
      </ModalLayout>
    );
  }

  // UI booleans based on the result:
  // - policyBlocks: are passkeys policy-blocked (admin etc)
  // - atMax: has user hit passkey quota
  const policyBlocks = passkeyStatus?.allowedByPolicy === false;
  const atMax = (passkeyStatus?.credentialCount ?? 0) >= maxPasskeys;

  // Main passkey management UI - list, buttons, toggles, etc.
  return (
    <ModalLayout maxHeight="70vh">
      <ModalLayout.Content>
        <div className="flex flex-col gap-4">
          {/* Show message if passkeys are disabled by org/admin policy */}
          {policyBlocks && (
            <Typography.Paragraph type="secondary">
              {t('modal.passkeysForm.policyDisabled')}
            </Typography.Paragraph>
          )}

          {/* Show empty state if no credentials and not policy-blocked */}
          {!policyBlocks && passkeyStatus?.credentialCount === 0 && (
            <Empty description={t('modal.passkeysForm.empty')} />
          )}

          {/* List all registered credentials. */}
          {passkeyStatus && passkeyStatus.credentials.length > 0 && (
            <ul className="flex flex-col gap-3">
              {passkeyStatus.credentials.map((credential) => (
                <li
                  key={credential.credentialId}
                  className="border-gray-medium flex flex-col gap-1 rounded-md border p-3 sm:flex-row sm:items-start sm:justify-between"
                >
                  {/* Details: icon, device name, created/time, transports, etc */}
                  <div className="flex min-w-0 flex-1 gap-2">
                    <Icon
                      // If backup-eligible and backed-up, show shield. Else, show key.
                      name={
                        credential.isBackupEligible && credential.isBackedUp
                          ? 'ShieldCheckIcon'
                          : 'KeyIcon'
                      }
                      className="text-blue-dark mt-0.5 h-6 w-6 shrink-0"
                    />
                    <div className="min-w-0">
                      <div className="truncate font-medium">{credential.deviceName}</div>
                      <div className="text-gray-dark text-xs">
                        {t('modal.passkeysForm.registered')}:{' '}
                        {formatDate(credential.createdAt, locale)}
                      </div>
                      <div className="text-gray-dark text-xs">
                        {t('modal.passkeysForm.lastUsed')}:{' '}
                        {credential.lastUsedAt
                          ? formatDate(credential.lastUsedAt, locale)
                          : t('modal.passkeysForm.never')}
                      </div>
                      <div className="text-gray-dark text-xs">
                        {t('modal.passkeysForm.transport')}:{' '}
                        {transportSummary(credential.transports, t)}
                      </div>
                      {/* If passkey is "non-discoverable" (not passwordless), show warning. */}
                      {!credential.isDiscoverable && (
                        <div className="text-gray-dark text-xs">
                          {t('modal.passkeysForm.passwordless')}:{' '}
                          {t('modal.passkeysForm.passwordlessUnavailable')}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Per-passkey controls: passwordless toggle & action menu */}
                  <div className="flex shrink-0 flex-col items-end gap-2">
                    {/* Discovery/passwordless toggle (if supported) */}
                    {credential.isDiscoverable && (
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-gray-dark whitespace-nowrap">
                          {t('modal.passkeysForm.passwordless')}
                        </span>
                        <Switch
                          checked={credential.passwordlessEnabled}
                          size="small"
                          onChange={(checked) => {
                            void togglePasswordless(credential, checked);
                          }}
                        />
                      </div>
                    )}
                    {/* Rename/remove dropdown menu */}
                    <Dropdown
                      menu={{
                        items: rowMenu(credential),
                        onClick: ({ key, domEvent }) => {
                          domEvent.stopPropagation();
                          if (key === 'remove') {
                            // If this is the last credential, show stronger warning.
                            modal.confirm({
                              content:
                                passkeyStatus.credentialCount <= 1
                                  ? t('modal.passkeysForm.removeLastWarning')
                                  : t('modal.passkeysForm.removeConfirm', {
                                      name: credential.deviceName,
                                    }),
                              okButtonProps: { danger: true },
                              okText: t('modal.passkeysForm.remove'),
                              onOk: () => removeCredential(credential),
                              title: t('modal.passkeysForm.removeTitle'),
                            });
                          }
                        },
                      }}
                      trigger={['click']}
                    >
                      <Button
                        type="text"
                        aria-label={t('a11y.openMoreActions')}
                        icon={<Icon name="EllipsisVerticalIcon" className="h-5 w-5" />}
                      />
                    </Dropdown>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </ModalLayout.Content>

      {(!policyBlocks || onClose) && (
        <ModalLayout.Footer>
          <div
            className={
              policyBlocks
                ? 'flex justify-end gap-2'
                : 'flex flex-wrap items-center justify-between gap-2'
            }
          >
            {!policyBlocks && (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="primary"
                  disabled={atMax}
                  onClick={() => {
                    openAddPasskeyModal();
                  }}
                >
                  {t('modal.passkeysForm.addPasskey')}
                </Button>
                {atMax && (
                  <Typography.Text type="secondary" className="text-sm">
                    {t('modal.passkeysForm.maxReached', { max: maxPasskeys })}
                  </Typography.Text>
                )}
              </div>
            )}
            {onClose ? <Button onClick={onClose}>{t('common.close')}</Button> : null}
          </div>
        </ModalLayout.Footer>
      )}
    </ModalLayout>
  );
}
